import { useEffect, useState } from 'react';
import { IC, Svg } from '../art/icons.js';
import { api, ApiError } from '../lib/api.js';
import { fmt, num } from '../lib/format.js';
import { useNav } from '../lib/router.js';
import { useStore } from '../lib/store.js';
import { Scroll, TopBar } from '../components/ui.js';

/*
 * The Creator hub (Prototype D's V.creator; CLAUDE.md §4 "Creators"; docs/29): for customers in the creator group.
 * Everything comes from GET /api/me/creator; the numbers are the server's (CLAUDE.md §7.3). The page shows the tier,
 * the creator's code with Copy and what followers get, this month so far, the next payout, the creator credit, the
 * campaigns with their status, "Propose a giveaway", and the rules shown to creators.
 */

export interface CreatorHub {
  tier: string; code: string | null; codeBlurb: string | null; ratePct: number;
  month: { key: string; label: string; codeUsed: number; ordersCollected: number; salesPence: number; earnedPence: number };
  payout: { date: string; label: string; owedPence: number };
  credit: { monthlyPence: number; grantedPence: number; leftPence: number; balancePence: number };
  campaigns: { id: string; title: string; post: string | null; proposedPrize: string | null; prize: string | null; note: string | null; status: 'proposed' | 'approved' | 'declined' | 'paid'; createdAt: string }[];
}

const STATUS: Record<CreatorHub['campaigns'][number]['status'], { label: string; cls: string }> = {
  proposed: { label: 'Waiting', cls: 'out' },
  approved: { label: 'Approved', cls: 'good' },
  declined: { label: 'Declined', cls: 'out' },
  paid: { label: 'Paid', cls: 'good' },
};

/** The line under a campaign: the prize and the staff note once approved, else what was proposed. */
function campaignNote(c: CreatorHub['campaigns'][number]): string {
  if (c.status === 'proposed') return c.proposedPrize ? `Proposed: ${c.proposedPrize} · waiting for Tikkas to approve` : 'Waiting for Tikkas to approve';
  if (c.status === 'declined') return c.note || 'Not this time';
  return [c.prize ? `Offer: ${c.prize}` : null, c.note].filter(Boolean).join('. ');
}

export function CreatorHubPage() {
  const s = useStore();
  const { navigate } = useNav();
  const u = s.me?.customer;
  const [hub, setHub] = useState<CreatorHub | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'not_creator' | 'error'>('loading');
  const load = async () => {
    try {
      setHub(await api<CreatorHub>('/api/me/creator'));
      setState('ok');
    } catch (e) {
      setState(e instanceof ApiError && e.status === 404 ? 'not_creator' : 'error');
    }
  };
  useEffect(() => {
    if (s.meLoaded && !u) navigate('/join?next=/creator', { replace: true });
    else if (u) void load();
  }, [u?.id, s.meLoaded]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!u) return null;
  return (
    <Scroll padStyle={{ gap: 18, paddingTop: 16 }}>
      <TopBar title="Creator hub" />
      {state === 'loading' && <div className="card empty" role="status"><b>Loading…</b></div>}
      {state === 'not_creator' && <div className="card empty"><b style={{ fontSize: 17 }}>Not a creator yet</b><span className="small muted">The Creator hub is for creators Tikkas works with. Ask us at the counter or by email if you'd like to be one.</span></div>}
      {state === 'error' && <div className="card empty"><b>Couldn't load the hub</b><span className="small muted">Check your connection and try again.</span><button type="button" className="pillbtn" onClick={() => void load()}>Try again</button></div>}
      {hub && <Hub hub={hub} reload={load} />}
    </Scroll>
  );
}

function Hub({ hub, reload }: { hub: CreatorHub; reload: () => Promise<void> }) {
  const s = useStore();
  const c = hub;
  const copy = async () => {
    if (!c.code) return;
    try {
      await navigator.clipboard.writeText(c.code);
      s.toast('Code copied');
    } catch {
      s.toast(c.code);
    }
  };
  return (
    <>
      <div className="row" style={{ gap: 8 }}><span className="badge violet">{c.tier}</span><span className="small muted">Added by Tikkas</span></div>
      {c.code ? (
        <div className="codecard"><span className="grow col" style={{ gap: 2, minWidth: 0 }}><span className="small muted">Your creator code</span><span className="code">{c.code}</span>{c.codeBlurb && <span className="small muted">{c.codeBlurb}</span>}</span><button type="button" className="copy" onClick={copy}>Copy</button></div>
      ) : (
        <div className="codecard"><span className="grow col" style={{ gap: 2 }}><span className="small muted">Your creator code</span><span className="code">Coming</span><span className="small muted">Tikkas is setting your code up</span></span></div>
      )}
      <section className="sec">
        <div className="sechead"><h2 className="h3">{c.month.label} so far</h2><span className="small muted">{c.ratePct}% commission</span></div>
        <div className="statgrid">
          <div className="card stat"><span className="small muted">Code used</span><b>{num(c.month.codeUsed)}</b></div>
          <div className="card stat"><span className="small muted">Orders collected</span><b>{num(c.month.ordersCollected)}</b></div>
          <div className="card stat"><span className="small muted">Sales from your code</span><b>{fmt(c.month.salesPence)}</b></div>
          <div className="card stat"><span className="small muted">Commission earned</span><b className="accent">{fmt(c.month.earnedPence)}</b></div>
        </div>
        <div className="card rw"><span className="grow col" style={{ gap: 2 }}><b style={{ fontSize: 14 }}>Next payout · {c.payout.label}</b><span className="small muted">{fmt(Math.max(0, c.payout.owedPence))} to your bank · paid on collected orders after refunds</span></span></div>
      </section>
      <div className="card rw"><span className="grow col" style={{ gap: 2 }}><b style={{ fontSize: 14 }}>Creator credit</b><span className="small muted">{fmt(c.credit.monthlyPence)} a month to eat at Tikkas<br />{fmt(c.credit.leftPence)} left this month</span></span><span className="ricon" style={{ color: 'var(--ink)' }}><Svg html={IC.wallet} /></span></div>
      <section className="sec">
        <div className="sechead"><h2 className="h3">Campaigns</h2></div>
        {c.campaigns.length > 0 && (
          <div className="card">{c.campaigns.map((x) => (
            <div className="rw" key={x.id}><span className="grow col" style={{ gap: 2, minWidth: 0 }}><b style={{ fontSize: 14 }}>{x.title}</b><span className="small muted">{campaignNote(x)}</span></span><span className={`badge ${STATUS[x.status].cls}`}>{STATUS[x.status].label}</span></div>
          ))}</div>
        )}
        <Propose onDone={reload} />
      </section>
      <p className="small muted" style={{ margin: 0, lineHeight: 1.5 }}>Label every post #ad. Commission is paid on collected orders only, after refunds. Tikkas approves giveaways and sets the prize.</p>
    </>
  );
}

/** "Propose a giveaway": a short form (title, what you'd post, the prize you'd like). It lands with Tikkas as Proposed. */
function Propose({ onDone }: { onDone: () => Promise<void> }) {
  const s = useStore();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [post, setPost] = useState('');
  const [prize, setPrize] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!open) return <button type="button" className="yellow" style={{ fontSize: 15 }} onClick={() => setOpen(true)}>Propose a giveaway</button>;
  const send = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api('/api/me/creator/campaigns', { body: { title, post, prize } });
      s.toast('Sent to Tikkas');
      setOpen(false);
      setTitle('');
      setPost('');
      setPrize('');
      await onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not send it');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="card col" style={{ padding: 16, gap: 10 }} onSubmit={(e) => { e.preventDefault(); if (!busy) void send(); }} aria-label="Propose a giveaway">
      <b style={{ fontSize: 15 }}>Propose a giveaway</b>
      <label className="col" style={{ gap: 6, fontSize: 13, fontWeight: 600 }}>Title<input className="txt-in" type="text" maxLength={80} placeholder="e.g. Giveaway reel" value={title} onChange={(e) => setTitle(e.target.value)} /></label>
      <label className="col" style={{ gap: 6, fontSize: 13, fontWeight: 600 }}>What you'd post<textarea className="note-in" maxLength={600} placeholder="A reel of the Angara bowl, winner picked on Friday" value={post} onChange={(e) => setPost(e.target.value)} /></label>
      <label className="col" style={{ gap: 6, fontSize: 13, fontWeight: 600 }}>Prize you'd like<input className="txt-in" type="text" maxLength={200} placeholder="e.g. 10 free Rice Bowls" value={prize} onChange={(e) => setPrize(e.target.value)} /></label>
      <span className="small muted">Tikkas approves giveaways and sets the prize.</span>
      {err && <span className="err" role="alert">{err}</span>}
      <span className="row" style={{ gap: 8 }}>
        <button type="submit" className="yellow grow" style={{ fontSize: 15, minHeight: 50 }} disabled={busy || title.trim().length < 3 || post.trim().length < 3}>{busy ? 'Sending…' : 'Send to Tikkas'}</button>
        <button type="button" className="pillbtn line" onClick={() => setOpen(false)}>Cancel</button>
      </span>
    </form>
  );
}
